import { useState } from "react";
import { api } from "../../api/apiClient";
import type { MeResponse } from "../../api/types";
import { initials } from "../team/teamLabels";
import { ConfirmEmailNudge } from "./ConfirmEmailNudge";

interface Props {
  me: MeResponse;
  onEdit: () => void;
  /** The pencil's panel is open — the pencil reads as pressed. */
  editing: boolean;
  showContract: boolean;
}

/**
 * Who you are, at the top of My profile, with the pencil that opens
 * `EditProfilePanel` (details, sign-in email, password).
 *
 * G-145, OD-224 (owner agreed 2026-10-13): until the address is confirmed, a
 * "Not confirmed" badge and **Confirm my email** sit right beside it. It is a
 * one-time step that "Forgot password?" depends on, so it is not hidden behind
 * the pencil; once confirmed both disappear and only the pencil is left.
 */
export function ProfileIdentityCard({ me, onEdit, editing, showContract }: Props) {
  const [contractBusy, setContractBusy] = useState(false);
  const [contractError, setContractError] = useState<string | null>(null);

  const openContract = async () => {
    setContractError(null);
    setContractBusy(true);
    try {
      const url = await api.fetchUserWorkContractBlobUrl(me.id);
      if (!url) {
        setContractError("Could not open contract PDF.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      // The new tab holds its own reference; free ours once it has loaded.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setContractBusy(false);
    }
  };

  return (
    <div className="card profile-id">
      <div className="profile-id-head">
        <div aria-hidden className="profile-id-avatar">
          {initials(me)}
        </div>

        <div className="profile-id-who">
          <div className="profile-id-title">
            <h2 className="profile-id-name">{me.fullName}</h2>
            <span className="badge primary profile-id-role">{me.role}</span>
          </div>
          <div className="profile-id-email-row">
            <span className="profile-id-email" title={me.email}>
              {me.email}
            </span>
            {me.emailVerified === false ? <ConfirmEmailNudge /> : null}
          </div>
          {me.pendingEmail ? (
            <p className="muted profile-id-note">Changing to {me.pendingEmail}</p>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit my profile"
          aria-expanded={editing}
          title="Edit my profile"
          className={editing ? "icon-button profile-id-edit is-active" : "icon-button profile-id-edit"}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
          </svg>
        </button>
      </div>

      <dl className="profile-id-facts">
        <dt className="muted">Phone</dt>
        <dd>{me.phoneNumber || "—"}</dd>
        <dt className="muted">Job title</dt>
        <dd>{me.jobTitle || "—"}</dd>
        {showContract ? (
          <>
            <dt className="muted">Contract</dt>
            <dd>
              {me.workContractConfigured ? (
                <button
                  type="button"
                  className="button plain profile-id-link"
                  onClick={() => void openContract()}
                  disabled={contractBusy}
                >
                  {contractBusy ? "Opening…" : "View PDF"}
                </button>
              ) : (
                <span className="muted">Not uploaded</span>
              )}
            </dd>
          </>
        ) : null}
      </dl>
      {contractError ? <div className="alert error">{contractError}</div> : null}
    </div>
  );
}
